import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Pages
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import Players from "./pages/Players";
import Sections from "./pages/Sections";
import MusicLibrary from "./pages/MusicLibrary";
import Notifications from "./pages/Notifications";
import Reports from "./pages/Reports";
import MissingPlayers from "./pages/MissingPlayers";
import Roster from "./pages/Roster";
import PushDiagnostic from "./pages/PushDiagnostic";
import Profile from "./pages/Profile";
import Achievements from "./pages/Achievements";
import Settings from "./pages/Settings";
import MembersRoles from "./pages/MembersRoles";
import Subscription from "./pages/Subscription";
import SystemAdmin from "./pages/SystemAdmin";
import SystemAdminMarketing from "./pages/SystemAdminMarketing";
import Impersonate from "./pages/Impersonate";
import Suspended from "./pages/Suspended";
import PlayerGuide from "./pages/PlayerGuide";
import AdminGuide from "./pages/AdminGuide";
import AllBandsCalendar from "./pages/AllBandsCalendar";
import AdminHome from "./pages/AdminHome";
import AdminImports from "./pages/AdminImports";
import AdminMonthlyDigest from "./pages/AdminMonthlyDigest";
import Finance from "./pages/Finance";
import Invoices from "./pages/Invoices";
import Inventory from "./pages/Inventory";
import Documents from "./pages/Documents";
import AdminDocuments from "./pages/AdminDocuments";
import Chat from "./pages/Chat";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import TermsOfService from "./pages/TermsOfService";
import Support from "./pages/Support";
import { AdminModeProvider } from "./AdminMode";
import { ThemeProvider } from "./ThemeContext";
import MyData from "./pages/MyData";
import MyMemberships from "./pages/MyMemberships";
import LandingPage from "./pages/LandingPage";
import NativePreview from "./pages/NativePreview";
import InvoicePay from "./pages/InvoicePay";
import CookieConsentBanner from "./components/CookieConsentBanner";
import Layout from "./components/Layout";
import { trySilentAutoSubscribe } from "./lib/pushManager";

// iter-128b: single source of truth for API base URL + axios client.
// Same-origin by default (see src/lib/api.js). Re-exported here to keep
// the ~36 consumer files that `import { apiClient, API } from "../App"`
// working without a bulk rewrite.
import {
  API as _API,
  apiClient as _apiClient,
} from "./lib/api";
export const API = _API;
export const apiClient = _apiClient;

// Auth Context — moved to AuthContext.js to break the App.js ↔ AdminMode.jsx
// circular import. Re-exported here for backwards-compat with the 36 consumer
// files that already do `import { useAuth } from "../App"`.
import { AuthContext, useAuth as _useAuth } from "./AuthContext";
export { AuthContext };
export const useAuth = _useAuth;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary text-xl font-heading">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

/** Gate for `/` — logged-out visitors see the marketing landing page at `/`
 *  but get sent to /login for any deeper path. Logged-in users always see the app. */
const RootGate = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary text-xl font-heading">Loading...</div>
      </div>
    );
  }
  if (!user) {
    if (location.pathname === "/") return <LandingPage />;
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token. iter-107s: wrapped in async IIFE so the linter
      // (react-hooks/set-state-in-effect) is satisfied — setState calls
      // happen via an awaited promise resolution rather than inside a
      // .then() chain that the new strict rule flags.
      (async () => {
        try {
          const res = await apiClient.get("/auth/me");
          setUser(res.data);
          localStorage.setItem("user", JSON.stringify(res.data));
        } catch (_e) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setUser(null);
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setLoading(false);
    }
  }, []);

  // Silent auto-subscribe to push: runs whenever an authenticated session is
  // established. No-op unless the browser already has 'granted' permission —
  // covers reinstall / cleared-data / new-device-with-OS-permission cases.
  // For brand-new users who've never been asked, the dashboard banner takes
  // over (Notification.requestPermission requires a user gesture).
  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => { trySilentAutoSubscribe(); }, 800);
    return () => clearTimeout(t);
  }, [user?.id]);

  const login = async (email, password) => {
    const res = await apiClient.post("/auth/login", { email, password });
    const { access_token } = res.data;
    localStorage.setItem("token", access_token);
    
    // Fetch full user data including subscription info
    const meRes = await apiClient.get("/auth/me");
    const fullUserData = meRes.data;
    localStorage.setItem("user", JSON.stringify(fullUserData));
    setUser(fullUserData);
    return fullUserData;
  };

  const register = async (name, email, password, band_name = null, join_code = null, band_type = null, section_id = null, consents = {}) => {
    const res = await apiClient.post("/auth/register", { 
      name, 
      email, 
      password, 
      role: "player",
      band_name,
      band_type,
      join_code,
      section_id,
      consent_terms: !!consents.terms,
      consent_privacy: !!consents.privacy,
      consent_notifications: !!consents.notifications,
      consent_data_sharing: !!consents.data_sharing,
    });
    const { access_token, user: userData } = res.data;
    localStorage.setItem("token", access_token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await apiClient.get("/auth/me");
      setUser(res.data);
      localStorage.setItem("user", JSON.stringify(res.data));
      return res.data;
    } catch (error) {
      console.error("Failed to refresh user:", error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// iter-107s: Listens for `sw-navigate` postMessage events from the
// Service Worker (fired when a user clicks a push notification while
// the SPA is already open). Translates the bare path into a React
// Router navigate() call so we get an instant in-app transition
// instead of a slow full-page reload — important for mobile / PWAs.
//
// Mounted inside <BrowserRouter> so `useNavigate()` has context.
function ServiceWorkerNavListener() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const handler = (event) => {
      const msg = event?.data;
      if (!msg || msg.type !== "sw-navigate") return;
      const url = msg.url || "/";
      // Only accept same-origin relative paths to avoid open-redirect
      // shenanigans. The SW already normalises to a path before posting,
      // but be defensive in case a future caller forwards anything weirder.
      if (typeof url !== "string" || !url.startsWith("/")) return;
      try {
        navigate(url);
      } catch (e) {
        // Last-resort: hard navigate
        window.location.href = url;
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [navigate]);
  return null;
}

function App() {
  // iter-125b: defensive orientation unlock. Some Android PWA wrappers
  // (notably Samsung's Add-to-Home from Samsung Internet) cache an
  // implicit portrait lock even when the manifest says
  // ``"orientation": "any"``. Calling unlock() at app boot releases that
  // lock so all pages rotate freely. No-op when no lock was applied or
  // when the API is unavailable (older browsers).
  useEffect(() => {
    try {
      if (typeof window !== "undefined"
          && window.screen
          && window.screen.orientation
          && typeof window.screen.orientation.unlock === "function") {
        window.screen.orientation.unlock();
      }
    } catch (_) { /* silent — best-effort only */ }
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
      <AdminModeProvider>
        <BrowserRouter>
        <ServiceWorkerNavListener />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/support" element={<Support />} />
          <Route path="/system-admin" element={<SystemAdmin />} />
          <Route path="/system-admin/marketing" element={<SystemAdminMarketing />} />
          <Route path="/impersonate" element={<Impersonate />} />
          <Route path="/suspended" element={<Suspended />} />
          <Route path="/preview/native" element={<NativePreview />} />
          <Route path="/invoices/:id/pay" element={<InvoicePay />} />
          <Route path="/" element={
            <RootGate>
              <Layout />
            </RootGate>
          }>
            <Route index element={<Dashboard />} />
            <Route path="events" element={<Events />} />
            <Route path="events/:eventId" element={<EventDetail />} />
            <Route path="players" element={<Players />} />
            <Route path="sections" element={<Sections />} />
            <Route path="music" element={<MusicLibrary />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="reports" element={<Reports />} />
            <Route path="missing-players" element={<MissingPlayers />} />
            <Route path="admin/roster" element={<Roster />} />
            <Route path="admin/push-diagnostic" element={<PushDiagnostic />} />
            <Route path="profile" element={<Profile />} />
            <Route path="achievements" element={<Achievements />} />
            <Route path="settings" element={<Settings />} />
            <Route path="members-roles" element={<MembersRoles />} />
            <Route path="subscription" element={<Subscription />} />
            <Route path="subscription/success" element={<Subscription />} />
            <Route path="subscription/cancel" element={<Subscription />} />
            <Route path="help/player" element={<PlayerGuide />} />
            <Route path="help/admin" element={<AdminGuide />} />
            <Route path="calendar/all" element={<AllBandsCalendar />} />
            <Route path="admin" element={<AdminHome />} />
            <Route path="admin/imports" element={<AdminImports />} />
            <Route path="admin/digest" element={<AdminMonthlyDigest />} />
            <Route path="admin/finance" element={<Finance />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="admin/inventory" element={<Inventory />} />
            <Route path="admin/documents" element={<AdminDocuments />} />
            <Route path="documents" element={<Documents />} />
            <Route path="chat" element={<Chat />} />
            <Route path="my-data" element={<MyData />} />
            <Route path="my-memberships" element={<MyMemberships />} />
          </Route>
        </Routes>
        <CookieConsentBanner />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      </AdminModeProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
